import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSettingsStore } from '../store/settingsStore';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function applyTheme(theme: Theme) {
  if (typeof window === 'undefined') return;

  const root = window.document.documentElement;
  const isDark = theme === 'dark';

  root.classList.remove(isDark ? 'light' : 'dark');
  root.classList.add(theme);
  localStorage.setItem('theme', theme);
}

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';

  // First check localStorage
  const storedTheme = localStorage.getItem('theme') as Theme;
  if (storedTheme) return storedTheme;

  // Then check system preference
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { settings, updateSettings } = useSettingsStore();
  const [theme, setThemeState] = useState<Theme>(() => {
    // Access theme directly from settings, not from nested preferences
    return settings?.theme as Theme || getInitialTheme();
  });

  // Initialize theme
  useEffect(() => {
    applyTheme(theme);
  }, []);

  // Sync with settings changes
  useEffect(() => {
    // Access theme directly from settings, not from nested preferences
    if (settings?.theme && settings.theme !== theme) {
      setThemeState(settings.theme as Theme);
      applyTheme(settings.theme as Theme);
    }
  }, [settings?.theme]);

  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);

    // Update settings if user is logged in
    if (settings?.user_id) {
      try {
        await updateSettings(settings.user_id, {
          theme: newTheme
        });
      } catch (error) {
        console.error('Failed to update theme in settings:', error);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
